import { getBareApi, unwrap } from '@/api/base-client'
import { backendUrl } from '@/shared/config/runtime'
import type { AccessTokenResponse } from '@/api/types'

/** A response to a refresh, which carries a new refresh token only when the
 * backend is configured to rotate them. */
export type RefreshResponse = AccessTokenResponse & { refresh_token?: string }

export async function requestTokenRefresh(
  refreshToken: string,
): Promise<RefreshResponse> {
  return await unwrap(
    getBareApi().POST('/api/user/token-refresh/', {
      body: { refresh_token: refreshToken },
    }),
  )
}

/**
 * Revokes a refresh token server side.
 *
 * Sent with `keepalive` and not awaited, because the only caller navigates the
 * browser to the identity provider on the next line. An ordinary request is
 * cancelled by that navigation, which is how a sign-out ends up leaving a usable
 * refresh token behind.
 */
export function blacklistRefreshToken(refreshToken: string): void {
  void fetch(`${backendUrl()}/api/user/token-blacklist/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
    keepalive: true,
  }).catch(() => {
    // Nothing useful to do: the page is already leaving. The token expires on its
    // own, and the identity provider's session -- the one that matters for signing
    // back in without being asked -- is ended by the redirect that follows.
  })
}

/**
 * One way in, as `/api/auth-provider/login-options/` describes it.
 *
 * Only `type` is guaranteed. The development password seam contributes an entry
 * with nothing else, because the form it used to sit beside no longer exists --
 * so an option is only offerable once it is known to carry a `redirect_url`.
 */
export interface LoginOption {
  type: string
  name?: string
  redirect_url?: string
}

/** A login option the browser can actually be sent to. */
export interface RedirectingLoginOption extends LoginOption {
  name: string
  redirect_url: string
}

/**
 * The ways in that this instance offers, keyed by provider type.
 *
 * Empty is a real answer and means the instance has no identity provider
 * configured, which the sign-in page reports rather than showing a dead button.
 */
export async function fetchLoginOptions(): Promise<Record<string, LoginOption>> {
  return await unwrap(getBareApi().GET('/api/auth-provider/login-options/'))
}

/**
 * Where the browser goes to end the session at the identity provider as well as
 * here. The backend owns this because it is the only side that knows the realm's
 * end-session endpoint, and it falls back to the signed-out page when there is no
 * provider configured -- so it is safe to call on a development instance too.
 */
export function signOutUrl(): string {
  return `${backendUrl()}/api/sso/oauth2/logout/`
}
